import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import type { ComponentPropsWithRef, ElementType, ReactNode } from 'react';
type RootProps = ComponentPropsWithRef<'div'>;

// A header brings its own top spacing, so the ground pads its top edge only when there is none.
const Root = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cx("w-full rounded-lg bg-secondary px-1 pb-1 pt-1 has-[>[data-card-header]]:pt-0", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
type HeaderProps = {
  title: ReactNode;
  rightContent?: ReactNode;
};
const Header = t0 => {
  const $ = _c(7);
  const {
    title,
    rightContent
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = typeof title === "string" ? <h3 className="text-label-2 text-primary max-md:min-w-0 max-md:break-words">{title}</h3> : title;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== rightContent) {
    t2 = typeof rightContent === "string" ? <div className="text-body-2 text-tertiary">{rightContent}</div> : rightContent;
    $[2] = rightContent;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div data-card-header={true} className="flex h-[40px] items-center justify-between px-2 max-md:h-auto max-md:min-h-[40px] max-md:gap-2 max-md:py-1">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
type BodyElement = 'div' | 'li' | 'section';
type BodyProps<T extends BodyElement = 'div'> = {
  as?: T;
} & ComponentPropsWithRef<T>;
const bodyClassName = 'rounded-default border border-solid border-tertiary bg-primary';
const Body = t0 => {
  const $ = _c(10);
  let as;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      as,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = as;
    $[2] = className;
    $[3] = props;
  } else {
    as = $[1];
    className = $[2];
    props = $[3];
  }
  const Element = (as ?? "div") as ElementType;
  let t1;
  if ($[4] !== className) {
    t1 = cx(bodyClassName, className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== Element || $[7] !== props || $[8] !== t1) {
    t2 = <Element className={t1} {...props} />;
    $[6] = Element;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
type ListProps = ComponentPropsWithRef<'ul'>;
const List = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cx("flex flex-col gap-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ul className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
type EmptyProps = {
  children: ReactNode;
  role?: 'status';
};
const Empty = t0 => {
  const $ = _c(4);
  const {
    children,
    role
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cx(bodyClassName, "px-4 py-6 text-center text-body-2 text-tertiary");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children || $[2] !== role) {
    t2 = <p role={role} className={t1}>{children}</p>;
    $[1] = children;
    $[2] = role;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const Card = {
  Root,
  Header,
  Body,
  List,
  Empty
};
export type { BodyProps as CardBodyProps, HeaderProps as CardHeaderProps, RootProps as CardRootProps };
export default Card;